import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { formatNumber } from '../../../utils'
import type { PerformanceColaboradorLinha } from '../tipos/performance'

const COR_ESPERADAS = '#94a3b8'
const COR_CLOCKIFY = '#465fff'
const COR_FATURADAS = '#059669'

function fmtHorasTooltip(value: number | string | (number | string)[] | undefined) {
  if (value === undefined || value === null)
    return '—'
  const v = Array.isArray(value) ? value[0] : value
  const n = typeof v === 'number' ? v : Number(v)
  if (Number.isNaN(n))
    return String(v)
  return `${formatNumber(n)} h`
}

function labelSerieHoras(dataKey: string) {
  if (dataKey === 'horasEsperadas')
    return 'H. esperadas'
  if (dataKey === 'horasTrabalhadas')
    return 'H. Clockify'
  if (dataKey === 'horasFaturadas')
    return 'H. faturadas'
  return dataKey
}

export type LinhaTotaisTime = {
  nomeTime: string
  horasEsperadas: number
  horasTrabalhadas: number
  horasFaturadas: number
  nomesTickets: string[]
}

export function GraficoTotaisPorTime({ dados }: { dados: LinhaTotaisTime[] }) {
  if (dados.length === 0)
    return null

  return (
    <div className="w-full h-[320px] min-h-[280px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={dados}
          margin={{ top: 8, right: 8, left: 8, bottom: 64 }}
          barGap={4}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
          <XAxis
            dataKey="nomeTime"
            tick={{ fontSize: 11, fill: '#374151' }}
            interval={0}
            angle={-35}
            textAnchor="end"
            height={70}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickFormatter={(v) => (typeof v === 'number' ? formatNumber(v) : String(v))}
            label={{ value: 'Horas', angle: -90, position: 'insideLeft', fill: '#6b7280', fontSize: 11 }}
          />
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload?.length)
                return null
              const row = dados.find((d) => d.nomeTime === label)
              return (
                <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-theme-sm max-w-md">
                  <p className="font-medium text-gray-900">Time: {label}</p>
                  {row?.nomesTickets && row.nomesTickets.length > 0 && (
                    <p className="mt-1 text-xs leading-relaxed text-gray-600">
                      {row.nomesTickets.join(' · ')}
                    </p>
                  )}
                  <ul className="mt-2 space-y-0.5 text-xs">
                    {payload.map((p) => (
                      <li key={String(p.dataKey)} className="flex justify-between gap-4">
                        <span style={{ color: p.color }}>{labelSerieHoras(String(p.dataKey))}</span>
                        <span className="font-medium text-gray-800">{fmtHorasTooltip(p.value)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            }}
          />
          <Legend
            wrapperStyle={{ paddingTop: 8 }}
            formatter={(value) => labelSerieHoras(String(value))}
          />
          <Bar dataKey="horasEsperadas" name="horasEsperadas" fill={COR_ESPERADAS} radius={[4, 4, 0, 0]} maxBarSize={36} />
          <Bar dataKey="horasTrabalhadas" name="horasTrabalhadas" fill={COR_CLOCKIFY} radius={[4, 4, 0, 0]} maxBarSize={36} />
          <Bar dataKey="horasFaturadas" name="horasFaturadas" fill={COR_FATURADAS} radius={[4, 4, 0, 0]} maxBarSize={36} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

type LinhaColab = {
  nomeCurto: string
  nomeCompleto: string
  nomesTickets: string[]
  horasEsperadas: number
  horasTrabalhadas: number
  horasFaturadas: number
}

function truncarNome(nome: string, max = 28) {
  const t = nome.trim()
  if (t.length <= max)
    return { curto: t, completo: t }
  return { curto: `${t.slice(0, max - 1)}…`, completo: t }
}

export function GraficoColaboradoresDoTime({ membros }: { membros: PerformanceColaboradorLinha[] }) {
  const data: LinhaColab[] = membros.map((m) => {
    const { curto, completo } = truncarNome(m.nome)
    return {
      nomeCurto: curto,
      nomeCompleto: completo,
      nomesTickets: m.nomesTickets ?? [],
      horasEsperadas: m.horasEsperadas,
      horasTrabalhadas: m.horasTrabalhadas,
      horasFaturadas: m.horasFaturadas,
    }
  })

  const altura = Math.min(520, Math.max(200, 48 + data.length * 44))

  return (
    <div className="w-full" style={{ height: altura }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          layout="vertical"
          data={data}
          margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
          barGap={2}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
          <XAxis
            type="number"
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickFormatter={(v) => (typeof v === 'number' ? formatNumber(v) : String(v))}
          />
          <YAxis
            type="category"
            dataKey="nomeCurto"
            width={140}
            interval={0}
            tick={{ fontSize: 11, fill: '#374151' }}
          />
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload?.length)
                return null
              const row = data.find((d) => d.nomeCurto === label)
              return (
                <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-theme-sm max-w-md">
                  <p className="font-medium text-gray-900">{row?.nomeCompleto ?? label}</p>
                  {row?.nomesTickets && row.nomesTickets.length > 0 && (
                    <p className="mt-1 text-xs leading-relaxed text-gray-600">
                      <span className="font-medium text-gray-700">Tickets: </span>
                      {row.nomesTickets.join(' · ')}
                    </p>
                  )}
                  <ul className="mt-2 space-y-0.5 text-xs">
                    {payload.map((p) => (
                      <li key={String(p.dataKey)} className="flex justify-between gap-4">
                        <span style={{ color: p.color }}>{labelSerieHoras(String(p.dataKey))}</span>
                        <span className="font-medium text-gray-800">{fmtHorasTooltip(p.value)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            }}
          />
          <Legend
            formatter={(value) => labelSerieHoras(String(value))}
          />
          <Bar dataKey="horasEsperadas" name="horasEsperadas" fill={COR_ESPERADAS} maxBarSize={22} radius={[2, 2, 2, 2]} />
          <Bar dataKey="horasTrabalhadas" name="horasTrabalhadas" fill={COR_CLOCKIFY} maxBarSize={22} radius={[2, 2, 2, 2]} />
          <Bar dataKey="horasFaturadas" name="horasFaturadas" fill={COR_FATURADAS} maxBarSize={22} radius={[2, 2, 2, 2]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
